import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import type { Editor } from '@tiptap/core'
import { useEditorState } from '@tiptap/react'
import {
  BLOCK_STYLES,
  FONT_FAMILIES,
  FONT_SIZE_PRESETS,
  HIGHLIGHT_COLORS,
  LINE_HEIGHT_PRESETS,
  TEXT_COLORS,
} from './editorPalette'
import {
  BULLET_LIST_STYLES,
  DEFAULT_FONT_FAMILY,
  DEFAULT_FONT_SIZE_PT,
  ORDERED_LIST_STYLES,
} from './editorExtensions'
import {
  AlignCenterIcon,
  AlignJustifyIcon,
  AlignLeftIcon,
  AlignRightIcon,
  BoldIcon,
  BulletListIcon,
  CaretDownIcon,
  ChecklistIcon,
  ClearFormatIcon,
  HighlightIcon,
  ImageIcon,
  ItalicIcon,
  LineSpacingIcon,
  LinkIcon,
  NumberedListIcon,
  RedoIcon,
  TextColorIcon,
  UnderlineIcon,
  UndoIcon,
} from './toolbarIcons'

type ListKind = 'bulletList' | 'orderedList' | 'taskList'

type ToolbarState = {
  blockStyle: string
  fontFamily: string
  fontSize: string
  color: string | null
  highlight: string | null
  bold: boolean
  italic: boolean
  underline: boolean
  link: boolean
  align: string
  lineHeight: string | null
  listKind: ListKind | null
  listStyle: string | null
  canUndo: boolean
  canRedo: boolean
}

/** 一次读完工具栏需要的全部状态（useEditorState 的选择器） */
function readToolbarState(editor: Editor): ToolbarState {
  const { $from } = editor.state.selection
  let blockNode: { type: { name: string }; attrs: Record<string, unknown> } | null = null
  let listNode: { type: { name: string }; attrs: Record<string, unknown> } | null = null

  for (let depth = $from.depth; depth > 0; depth -= 1) {
    const node = $from.node(depth)
    if (!blockNode && (node.type.name === 'paragraph' || node.type.name === 'heading')) {
      blockNode = node
    }
    if (
      !listNode
      && (node.type.name === 'bulletList'
        || node.type.name === 'orderedList'
        || node.type.name === 'taskList')
    ) {
      listNode = node
    }
  }

  const textStyle = editor.getAttributes('textStyle') as {
    color?: string
    fontFamily?: string
    fontSize?: string
  }
  const highlight = editor.getAttributes('highlight') as { color?: string }

  return {
    blockStyle: blockNode?.type.name === 'heading' ? `heading-${blockNode.attrs.level}` : 'paragraph',
    fontFamily: textStyle.fontFamily ?? DEFAULT_FONT_FAMILY,
    fontSize: textStyle.fontSize ?? `${DEFAULT_FONT_SIZE_PT}pt`,
    color: textStyle.color ?? null,
    highlight: highlight.color ?? null,
    bold: editor.isActive('bold'),
    italic: editor.isActive('italic'),
    underline: editor.isActive('underline'),
    link: editor.isActive('link'),
    align: (blockNode?.attrs.textAlign as string | undefined) ?? 'left',
    lineHeight: (blockNode?.attrs.lineHeight as string | undefined) ?? null,
    listKind: (listNode?.type.name as ListKind | undefined) ?? null,
    listStyle: (listNode?.attrs.listStyle as string | undefined) ?? null,
    canUndo: editor.can().undo(),
    canRedo: editor.can().redo(),
  }
}

function MenuItem({
  active,
  disabled,
  onClick,
  children,
  className,
}: {
  active?: boolean
  disabled?: boolean
  onClick: () => void
  children: ReactNode
  className?: string
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      className={`tb-item${active ? ' active' : ''}${className ? ` ${className}` : ''}`}
      // 关键：不要让点击把焦点从编辑器抢走，否则选区丢失
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

function ToolbarDropdown({
  label,
  title,
  children,
  className,
  disabled,
  width,
}: {
  label: ReactNode
  title: string
  children: (close: () => void) => ReactNode
  className?: string
  disabled?: boolean
  width?: number
}) {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const root = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const close = useCallback(() => setOpen(false), [])
  const panelWidth = width ?? 180

  function openPanel() {
    const rect = trigger.current?.getBoundingClientRect()
    if (rect) {
      setPosition({
        top: rect.bottom + 4,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - panelWidth - 8)),
      })
    }
    setOpen(true)
  }

  useEffect(() => {
    if (!open) return undefined
    function handlePointerDown(event: MouseEvent) {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false)
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    // 面板是 fixed 定位，工具栏一滚动就会错位，直接关掉
    function closeOnMove() {
      setOpen(false)
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    window.addEventListener('scroll', closeOnMove, true)
    window.addEventListener('resize', closeOnMove)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('scroll', closeOnMove, true)
      window.removeEventListener('resize', closeOnMove)
    }
  }, [open])

  return (
    <div className={`tb-dropdown${open ? ' open' : ''}`} ref={root}>
      <button
        ref={trigger}
        type="button"
        className={className ?? 'tb-trigger'}
        title={title}
        aria-label={title}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => (open ? close() : openPanel())}
      >
        {label}
      </button>
      {open && (
        <div
          className="tb-panel"
          role="menu"
          style={{ top: position.top, left: position.left, minWidth: panelWidth }}
        >
          {children(close)}
        </div>
      )}
    </div>
  )
}

function ToolbarButton({
  title,
  active,
  disabled,
  onClick,
  children,
}: {
  title: string
  active?: boolean
  disabled?: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      className={`tb-button${active ? ' active' : ''}`}
      title={title}
      aria-label={title}
      aria-pressed={active ?? false}
      disabled={disabled}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

function Divider() {
  return <span className="tb-divider" aria-hidden="true" />
}

function FontSizeControl({
  editor,
  value,
  disabled,
}: {
  editor: Editor
  value: string
  disabled?: boolean
}) {
  const parsed = Number.parseFloat(value)
  const current = Number.isFinite(parsed) ? parsed : DEFAULT_FONT_SIZE_PT
  const [draft, setDraft] = useState(String(current))

  useEffect(() => {
    setDraft(String(current))
  }, [current])

  function apply(size: number) {
    const clamped = Math.min(Math.max(size, 1), 400)
    editor.chain().focus().setFontSize(`${clamped}pt`).run()
  }

  function commitDraft() {
    const next = Number.parseFloat(draft)
    if (!Number.isFinite(next) || next <= 0) {
      setDraft(String(current))
      return
    }
    apply(next)
  }

  return (
    <div className="tb-fontsize">
      <button
        type="button"
        className="tb-button tb-stepper"
        title="减小字号"
        aria-label="减小字号"
        disabled={disabled}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => apply(current - 1)}
      >
        −
      </button>
      <input
        className="tb-size-input"
        value={draft}
        disabled={disabled}
        inputMode="decimal"
        aria-label="字号"
        onChange={(event) => setDraft(event.target.value)}
        onFocus={(event) => event.target.select()}
        onBlur={commitDraft}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            commitDraft()
            event.currentTarget.blur()
          }
          if (event.key === 'Escape') setDraft(String(current))
        }}
      />
      <ToolbarDropdown
        className="tb-caret-only"
        title="字号预设"
        disabled={disabled}
        width={150}
        label={<CaretDownIcon />}
      >
        {(close) => (
          <div className="tb-size-list">
            {FONT_SIZE_PRESETS.map((size) => (
              <MenuItem
                key={size}
                active={Math.abs(current - size) < 0.01}
                onClick={() => {
                  apply(size)
                  close()
                }}
              >
                {size}
              </MenuItem>
            ))}
          </div>
        )}
      </ToolbarDropdown>
      <button
        type="button"
        className="tb-button tb-stepper"
        title="增大字号"
        aria-label="增大字号"
        disabled={disabled}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => apply(current + 1)}
      >
        +
      </button>
    </div>
  )
}

function ColorPicker({
  editor,
  kind,
  value,
  disabled,
}: {
  editor: Editor
  kind: 'color' | 'highlight'
  value: string | null
  disabled?: boolean
}) {
  const palette = kind === 'color' ? TEXT_COLORS : HIGHLIGHT_COLORS
  const isText = kind === 'color'

  function applyColor(color: string) {
    if (isText) editor.chain().focus().setColor(color).run()
    else editor.chain().focus().toggleHighlight({ color }).run()
  }

  function clearColor() {
    if (isText) editor.chain().focus().unsetColor().run()
    else editor.chain().focus().unsetHighlight().run()
  }

  return (
    <ToolbarDropdown
      title={isText ? '文字颜色' : '突出显示颜色'}
      disabled={disabled}
      width={236}
      label={(
        <span className="tb-color-trigger">
          {isText ? <TextColorIcon /> : <HighlightIcon />}
          <span
            className="tb-color-bar"
            style={{ background: value ?? (isText ? '#000000' : '#f1f3f4') }}
          />
        </span>
      )}
    >
      {(close) => (
        <div className="tb-color-panel">
          {palette.map((row, rowIndex) => (
            <div className="tb-color-row" key={rowIndex}>
              {row.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`tb-swatch${value === color ? ' active' : ''}`}
                  style={{ background: color }}
                  title={color}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    applyColor(color)
                    close()
                  }}
                />
              ))}
            </div>
          ))}
          <div className="tb-color-actions">
            <MenuItem
              onClick={() => {
                clearColor()
                close()
              }}
            >
              无
            </MenuItem>
            <label className="tb-custom-color">
              自定义
              <input
                type="color"
                value={value ?? (isText ? '#000000' : '#ffff00')}
                onChange={(event) => applyColor(event.target.value)}
              />
            </label>
          </div>
        </div>
      )}
    </ToolbarDropdown>
  )
}

export function Toolbar({
  editor,
  canEdit,
  onPickImage,
}: {
  editor: Editor
  canEdit: boolean
  onPickImage: () => void
}) {
  const state = useEditorState({
    editor,
    selector: ({ editor: current }) => readToolbarState(current),
  })

  const disabled = !canEdit
  const activeBlockStyle = BLOCK_STYLES.find((style) => style.value === state.blockStyle) ?? BLOCK_STYLES[0]
  const activeFont = FONT_FAMILIES.find((font) => font.value === state.fontFamily)

  function setBlockStyle(value: string) {
    const target = BLOCK_STYLES.find((style) => style.value === value)
    if (!target) return
    const chain = editor.chain().focus()
    if (target.level === null) chain.setParagraph().run()
    else chain.setHeading({ level: target.level }).run()
  }

  function setLink() {
    const previous = editor.getAttributes('link').href as string | undefined
    const value = window.prompt('链接地址', previous ?? 'https://')
    if (value === null) return
    if (!value.trim()) editor.chain().focus().unsetLink().run()
    else editor.chain().focus().extendMarkRange('link').setLink({ href: value.trim() }).run()
  }

  return (
    <div className="docs-toolbar" role="toolbar" aria-label="格式工具栏">
      <ToolbarButton
        title="撤销 (Ctrl+Z)"
        disabled={disabled || !state.canUndo}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <UndoIcon />
      </ToolbarButton>
      <ToolbarButton
        title="重做 (Ctrl+Shift+Z)"
        disabled={disabled || !state.canRedo}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <RedoIcon />
      </ToolbarButton>

      <Divider />

      <ToolbarDropdown
        className="tb-trigger tb-style-trigger"
        title="段落样式"
        disabled={disabled}
        width={220}
        label={(
          <span className="tb-trigger-label">
            <span className={activeBlockStyle.previewClass}>{activeBlockStyle.label}</span>
            <CaretDownIcon />
          </span>
        )}
      >
        {(close) => (
          <>
            {BLOCK_STYLES.map((style) => (
              <MenuItem
                key={style.value}
                active={style.value === state.blockStyle}
                onClick={() => {
                  setBlockStyle(style.value)
                  close()
                }}
              >
                <span className={style.previewClass}>{style.label}</span>
              </MenuItem>
            ))}
          </>
        )}
      </ToolbarDropdown>

      <Divider />

      <ToolbarDropdown
        className="tb-trigger tb-font-trigger"
        title="字体"
        disabled={disabled}
        width={210}
        label={(
          <span className="tb-trigger-label">
            <span style={{ fontFamily: state.fontFamily }}>{activeFont?.label ?? state.fontFamily}</span>
            <CaretDownIcon />
          </span>
        )}
      >
        {(close) => (
          <>
            {FONT_FAMILIES.map((font) => (
              <MenuItem
                key={font.value}
                active={font.value === state.fontFamily}
                onClick={() => {
                  if (font.value === DEFAULT_FONT_FAMILY) editor.chain().focus().unsetFontFamily().run()
                  else editor.chain().focus().setFontFamily(font.value).run()
                  close()
                }}
              >
                <span style={{ fontFamily: font.value }}>{font.label}</span>
              </MenuItem>
            ))}
          </>
        )}
      </ToolbarDropdown>

      <Divider />

      <FontSizeControl editor={editor} value={state.fontSize} disabled={disabled} />

      <Divider />

      <ToolbarButton
        title="加粗 (Ctrl+B)"
        active={state.bold}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <BoldIcon />
      </ToolbarButton>
      <ToolbarButton
        title="斜体 (Ctrl+I)"
        active={state.italic}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <ItalicIcon />
      </ToolbarButton>
      <ToolbarButton
        title="下划线 (Ctrl+U)"
        active={state.underline}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <UnderlineIcon />
      </ToolbarButton>

      <ColorPicker editor={editor} kind="color" value={state.color} disabled={disabled} />
      <ColorPicker editor={editor} kind="highlight" value={state.highlight} disabled={disabled} />

      <Divider />

      <ToolbarButton title="插入链接" active={state.link} disabled={disabled} onClick={setLink}>
        <LinkIcon />
      </ToolbarButton>
      <ToolbarButton title="插入图片" disabled={disabled} onClick={onPickImage}>
        <ImageIcon />
      </ToolbarButton>

      <Divider />

      <ToolbarDropdown
        title="对齐方式"
        disabled={disabled}
        width={180}
        label={<AlignLeftIcon />}
      >
        {(close) => (
          <>
            <MenuItem
              active={state.align === 'left'}
              onClick={() => {
                editor.chain().focus().setTextAlign('left').run()
                close()
              }}
            >
              <AlignLeftIcon /> 左对齐
            </MenuItem>
            <MenuItem
              active={state.align === 'center'}
              onClick={() => {
                editor.chain().focus().setTextAlign('center').run()
                close()
              }}
            >
              <AlignCenterIcon /> 居中
            </MenuItem>
            <MenuItem
              active={state.align === 'right'}
              onClick={() => {
                editor.chain().focus().setTextAlign('right').run()
                close()
              }}
            >
              <AlignRightIcon /> 右对齐
            </MenuItem>
            <MenuItem
              active={state.align === 'justify'}
              onClick={() => {
                editor.chain().focus().setTextAlign('justify').run()
                close()
              }}
            >
              <AlignJustifyIcon /> 两端对齐
            </MenuItem>
          </>
        )}
      </ToolbarDropdown>

      <ToolbarDropdown title="行距" disabled={disabled} width={220} label={<LineSpacingIcon />}>
        {(close) => (
          <>
            {LINE_HEIGHT_PRESETS.map((preset) => (
              <MenuItem
                key={preset.value}
                active={state.lineHeight === preset.value}
                onClick={() => {
                  editor.chain().focus().setFocusLineHeight(preset.value).run()
                  close()
                }}
              >
                {preset.label}
              </MenuItem>
            ))}
            <MenuItem
              active={state.lineHeight === null}
              onClick={() => {
                editor.chain().focus().unsetFocusLineHeight().run()
                close()
              }}
            >
              恢复默认
            </MenuItem>
          </>
        )}
      </ToolbarDropdown>

      <Divider />

      <ToolbarButton
        title="待办清单"
        active={state.listKind === 'taskList'}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleTaskList().run()}
      >
        <ChecklistIcon />
      </ToolbarButton>

      <div className="tb-split">
        <ToolbarButton
          title="项目符号列表"
          active={state.listKind === 'bulletList'}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <BulletListIcon />
        </ToolbarButton>
        <ToolbarDropdown
          className="tb-caret-only"
          title="项目符号样式"
          disabled={disabled}
          width={170}
          label={<CaretDownIcon />}
        >
          {(close) => (
            <>
              {BULLET_LIST_STYLES.map((style) => (
                <MenuItem
                  key={style.value}
                  active={state.listStyle === style.value}
                  onClick={() => {
                    if (state.listKind !== 'bulletList') editor.chain().focus().toggleBulletList().run()
                    editor.chain().focus().setFocusListStyle(style.value).run()
                    close()
                  }}
                >
                  {style.label}
                </MenuItem>
              ))}
            </>
          )}
        </ToolbarDropdown>
      </div>

      <div className="tb-split">
        <ToolbarButton
          title="编号列表"
          active={state.listKind === 'orderedList'}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <NumberedListIcon />
        </ToolbarButton>
        <ToolbarDropdown
          className="tb-caret-only"
          title="编号样式"
          disabled={disabled}
          width={190}
          label={<CaretDownIcon />}
        >
          {(close) => (
            <>
              {ORDERED_LIST_STYLES.map((style) => (
                <MenuItem
                  key={style.value}
                  active={state.listStyle === style.value}
                  onClick={() => {
                    if (state.listKind !== 'orderedList') editor.chain().focus().toggleOrderedList().run()
                    editor.chain().focus().setFocusListStyle(style.value).run()
                    close()
                  }}
                >
                  {style.label}
                </MenuItem>
              ))}
            </>
          )}
        </ToolbarDropdown>
      </div>

      <Divider />

      <ToolbarButton
        title="清除格式 (Ctrl+\\)"
        disabled={disabled}
        onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
      >
        <ClearFormatIcon />
      </ToolbarButton>
    </div>
  )
}
